import React, { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../utils/cn';

interface PhotoGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
  darkMode?: boolean;
}

const STORAGE_KEY = 'photo_guidelines_dont_show_again';

// Helper function to check if guidelines should be shown
export const shouldShowPhotoGuidelines = (): boolean => {
  return localStorage.getItem(STORAGE_KEY) !== 'true';
};

// Helper function to reset the preference (for testing or settings)
export const resetPhotoGuidelinesPreference = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const PhotoGuidelinesModal: React.FC<PhotoGuidelinesModalProps> = ({ open, onClose }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleClose = () => {
    if (dontShowAgain) localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
  };

  if (!open) return null;

  const correctExamples = [
    { src: '/assets/photo-guidelines/correct-1.jpg', alt: 'Exemplo correto 1' },
    { src: '/assets/photo-guidelines/correct-2.jpg', alt: 'Exemplo correto 2' },
    { src: '/assets/photo-guidelines/correct-3.jpg', alt: 'Exemplo correto 3' },
  ];

  const incorrectExamples = [
    { src: '/assets/photo-guidelines/incorrect-1.jpg', alt: 'Óculos / Expressão incorreta' },
    { src: '/assets/photo-guidelines/incorrect-2.jpg', alt: 'Óculos / Foto sem cor' },
    { src: '/assets/photo-guidelines/incorrect-3.jpg', alt: 'Óculos' },
    { src: '/assets/photo-guidelines/incorrect-4.jpg', alt: 'Baixa iluminação' },
    { src: '/assets/photo-guidelines/incorrect-5.jpg', alt: 'Boné' },
    { src: '/assets/photo-guidelines/incorrect-6.jpg', alt: 'Boné / Ângulo incorreto' },
  ];

  const instructions = [
    {
      text: 'Fundo neutro — Utilize parede branca ou clara, sem sombras, objetos ou terceiros ao fundo.',
      type: 'do' as const,
    },
    {
      text: 'Iluminação adequada — Prefira luz natural difusa. Evite ambientes escuros ou com contraluz.',
      type: 'do' as const,
    },
    {
      text: 'Enquadramento centralizado — Rosto e pescoço totalmente visíveis, com o topo da cabeça dentro do quadro.',
      type: 'do' as const,
    },
    {
      text: 'Expressão neutra — Olhos abertos, boca fechada. Não sorrir.',
      type: 'do' as const,
    },
    {
      text: 'Rosto sem obstruções — Óculos (incluindo de grau), bonés, máscaras e fones de ouvido não são permitidos.',
      type: 'dont' as const,
      highlight: true,
    },
    {
      text: 'Distância adequada — Posicione-se entre 50 cm e 1 metro da câmera.',
      type: 'do' as const,
    },
    {
      text: 'Foto nítida e estática — Imagens borradas, em movimento, com filtros, cortadas ou espelhadas serão rejeitadas.',
      type: 'dont' as const,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-8 py-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-black tracking-wide text-gray-900 uppercase">
              CADASTRO DE IDENTIDADE FACIAL
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              O reconhecimento facial é a camada central de segurança do sistema. Uma foto fora dos padrões compromete sua identificação e pode impedir o acesso.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="ml-6 mt-0.5 text-gray-400 hover:text-gray-700 transition-colors p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto px-8 py-6 space-y-7"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f9fafb' }}
        >

          {/* Why it matters */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">
              POR QUE ISSO É IMPORTANTE
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Uma foto de qualidade é indispensável para que o sistema identifique você com precisão.
              Imagens fora dos padrões abaixo resultarão em falha no reconhecimento e impedimento de acesso.
            </p>
          </div>

          {/* Instructions */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
              DIRETRIZES DE CAPTURA
            </p>
            <div className="space-y-2">
              {instructions.map((instruction, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-start gap-3 p-3.5 rounded-lg border",
                    instruction.highlight
                      ? "bg-amber-50 border-amber-200"
                      : "bg-gray-50 border-gray-100"
                  )}
                >
                  {instruction.type === 'do' ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                  )}
                  <p className="text-sm text-gray-700 leading-relaxed">{instruction.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Examples */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
              EXEMPLOS VISUAIS
            </p>

            {/* Correct */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">CORRETO — FAÇA ASSIM</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {correctExamples.map((example, index) => (
                  <div
                    key={index}
                    className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-emerald-200"
                  >
                    <img src={example.src} alt={example.alt} className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 bg-emerald-500 rounded-full p-1">
                      <CheckCircle className="h-3.5 w-3.5 text-white" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Incorrect */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-xs font-bold text-red-600 uppercase tracking-wider">INCORRETO — EVITE ESTES CASOS</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {incorrectExamples.map((example, index) => (
                  <div
                    key={index}
                    className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-red-200"
                  >
                    <img src={example.src} alt={example.alt} className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 bg-red-500 rounded-full p-1">
                      <XCircle className="h-3.5 w-3.5 text-white" />
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-black/50 px-2 py-1.5">
                      <p className="text-xs text-white font-medium text-center truncate">{example.alt}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="border border-gray-200 rounded-lg p-5">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">SUPORTE</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Em caso de dúvidas sobre o cadastro de foto, entre em contato com a equipe responsável pelo sistema.
              Nossos especialistas garantem que o processo de reconhecimento facial funcione com precisão e segurança.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-200 bg-white">
          <div className="flex items-center justify-between gap-6">
            {/* Don't show again */}
            <label className="flex items-center gap-3 cursor-pointer group select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-5 h-5 rounded border-gray-400 text-gray-900 focus:ring-2 focus:ring-gray-400 focus:ring-offset-0 cursor-pointer accent-gray-900"
              />
              <span className="text-sm font-medium text-gray-500 group-hover:text-gray-800 transition-colors">
                Não exibir novamente
              </span>
            </label>

            {/* CTA */}
            <button
              onClick={handleClose}
              className="px-8 py-2.5 bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-colors tracking-wide uppercase"
            >
              Confirmar e Prosseguir
            </button>
          </div>
        </div>
      </div>

      {/* Scrollbar styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .flex-1.overflow-y-auto::-webkit-scrollbar { width: 6px; }
          .flex-1.overflow-y-auto::-webkit-scrollbar-track { background: #f9fafb; border-radius: 4px; }
          .flex-1.overflow-y-auto::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
          .flex-1.overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
        `
      }} />
    </div>
  );
};
